/**
 * Admin Feedback Page
 *
 * Admin-only page to view and manage user feedback
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../hooks/useAuth';
import type { Feedback, FeedbackStatus, FeedbackType } from '../types';
import '../styles/AdminFeedbackPage.css';

export default function AdminFeedbackPage() {
  const { user } = useAuth();
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<'all' | FeedbackType>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | FeedbackStatus>('all');
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const statusOptions: FeedbackStatus[] = ['new', 'in-progress', 'resolved', 'closed'];

  useEffect(() => {
    loadFeedback();
  }, [filterType, filterStatus]);

  const loadFeedback = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: { type?: FeedbackType; status?: FeedbackStatus } = {};
      if (filterType !== 'all') params.type = filterType;
      if (filterStatus !== 'all') params.status = filterStatus;

      const response = await api.getAllFeedback(params);
      setFeedback(response.feedback);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (feedbackId: string, newStatus: FeedbackStatus) => {
    try {
      await api.updateFeedbackStatus(feedbackId, newStatus);
      // Update local state
      setFeedback(prev =>
        prev.map(f => (f.id === feedbackId ? { ...f, status: newStatus } : f))
      );
      setSelectedFeedback(prev =>
        prev?.id === feedbackId ? { ...prev, status: newStatus } : prev
      );
    } catch (err) {
      alert('Failed to update status: ' + (err instanceof Error ? err.message : 'Unknown error'));
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const getTypeIcon = (type: FeedbackType) => {
    if (type === 'bug') {
      return (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
      );
    }
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2v20M2 12h20"/>
      </svg>
    );
  };

  const getStatusColor = (status: FeedbackStatus) => {
    switch (status) {
      case 'new':
        return '#64ffda';
      case 'in-progress':
        return '#ffd664';
      case 'resolved':
        return '#a3ff64';
      case 'closed':
        return '#888';
      default:
        return '#fff';
    }
  };

  // Check if user is admin
  if (user?.email !== 'arberodds@gmail.com') {
    return (
      <div className="admin-feedback-page">
        <div className="admin-unauthorized">
          <h1>Unauthorized</h1>
          <p>You don&apos;t have permission to access this page.</p>
          <Link to="/dashboard" className="back-link">Return to Dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-feedback-page">
      <div className="admin-feedback-container">
        {/* Header */}
        <div className="admin-feedback-header">
          <div>
            <h1>User Feedback</h1>
            <p>Manage bug reports and improvement suggestions</p>
          </div>
          <Link to="/dashboard" className="back-link">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back to Dashboard
          </Link>
        </div>

        {/* Filters */}
        <div className="admin-feedback-filters">
          <div className="filter-group">
            <label>Type:</label>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value as 'all' | FeedbackType)}>
              <option value="all">All Types</option>
              <option value="bug">Bug Reports</option>
              <option value="improvement">Improvements</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Status:</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value as 'all' | FeedbackStatus)}>
              <option value="all">All Statuses</option>
              <option value="new">New</option>
              <option value="in-progress">In Progress</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
          <div className="filter-stats">
            <span>{feedback.length} items</span>
          </div>
        </div>

        {/* Content */}
        <div className="admin-feedback-content">
          {/* Feedback List */}
          <div className="feedback-list">
            {loading ? (
              <div className="feedback-loading">Loading feedback...</div>
            ) : error ? (
              <div className="feedback-error">{error}</div>
            ) : feedback.length === 0 ? (
              <div className="feedback-empty">
                <div className="feedback-empty-icon">📬</div>
                <div>No feedback found</div>
              </div>
            ) : (
              feedback.map((item) => (
                <div
                  key={item.id}
                  className={`feedback-item ${selectedFeedback?.id === item.id ? 'selected' : ''}`}
                  onClick={() => setSelectedFeedback(item)}
                >
                  <div className="feedback-item-header">
                    <div className="feedback-item-type" data-type={item.type}>
                      {getTypeIcon(item.type)}
                      {item.type === 'bug' ? 'Bug' : 'Improvement'}
                    </div>
                    <div
                      className="feedback-item-status"
                      style={{ backgroundColor: getStatusColor(item.status) }}
                    >
                      {item.status}
                    </div>
                  </div>
                  <div className="feedback-item-title">{item.title}</div>
                  <div className="feedback-item-meta">
                    <span>{item.user_profiles.email}</span>
                    <span>•</span>
                    <span>{formatDate(item.created_at)}</span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Feedback Detail */}
          <div className="feedback-detail">
            {selectedFeedback ? (
              <>
                <div className="feedback-detail-header">
                  <div>
                    <div className="feedback-detail-type" data-type={selectedFeedback.type}>
                      {getTypeIcon(selectedFeedback.type)}
                      {selectedFeedback.type === 'bug' ? 'Bug Report' : 'Improvement Suggestion'}
                    </div>
                    <h2>{selectedFeedback.title}</h2>
                    <div className="feedback-detail-meta">
                      From: {selectedFeedback.user_profiles.email}
                      {selectedFeedback.user_profiles.full_name &&
                        ` (${selectedFeedback.user_profiles.full_name})`}
                      <br />
                      Submitted: {formatDate(selectedFeedback.created_at)}
                    </div>
                  </div>
                </div>

                <div className="feedback-detail-body">
                  <h3>Description</h3>
                  <p>{selectedFeedback.description}</p>
                </div>

                <div className="feedback-detail-actions">
                  <label>Update Status:</label>
                  <div className="status-buttons">
                    {statusOptions.map((status) => (
                      <button
                        key={status}
                        className={`status-btn ${
                          selectedFeedback.status === status ? 'active' : ''
                        }`}
                        style={{
                          backgroundColor:
                            selectedFeedback.status === status
                              ? getStatusColor(status)
                              : 'transparent',
                          borderColor: getStatusColor(status),
                          color:
                            selectedFeedback.status === status ? '#0a141d' : getStatusColor(status),
                        }}
                        onClick={() => handleStatusUpdate(selectedFeedback.id, status)}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            ) : (
              <div className="feedback-detail-empty">
                <div className="feedback-detail-empty-icon">👈</div>
                <p>Select a feedback item to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
